import React, { useEffect, useRef, useState } from "react";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import Popover from "@mui/material/Popover";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SortIcon from "@mui/icons-material/Sort";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SignalWifiStatusbarConnectedNoInternet4Icon from "@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";

import { GetFeature, getHasHigherPriorityZcAlert, getTaggedVehiclesSortTooltipFromSortValue } from "./mapUtil";
import { ConvertMetersToFeet } from "../util";

import { useMap } from "../contexts/mapContext";
import { useUserAuth } from "../contexts/authContext";

const MapTaggedVehicleCards = (props) => {
    const { user, socket, userProfileSettings, updateProfileSettings } = useUserAuth();

    const [zeroConflictConfigurations, setZeroConflictConfigurations] = useState([]);

    const [allTaggedVehicles, setAllTaggedVehicles] = useState([]);
    const [filteredTaggedVehicles, setFilteredTaggedVehicles] = useState([]);

    const [selectedSortListValue, setSelectedSortListValue] = useState(userProfileSettings.sidebar.vehicle_cards.sort);
    const [sortListAnchorEl, setSortListAnchorEl] = useState(null);
    const sortListOpen = Boolean(sortListAnchorEl);

    const { handleFailedFetch } = useUserAuth();

    useEffect(() => {
        fetchZeroConflictConfigurations();
    }, []);

    useEffect(() => {
        if (!socket) return;

        socket.on("update_tagged_vehicles", (taggedVehicles) => {
            setAllTaggedVehicles(taggedVehicles);
            handleSortVehiclesButtonClick(taggedVehicles, selectedSortListValue);
        });
        socket.emit("get_tagged_vehicles", user.id);

        return () => {
            if (!socket) return;
            socket.off("update_tagged_vehicles");
        };
    }, [socket, selectedSortListValue]);

    const fetchZeroConflictConfigurations = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/zca_configs/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setZeroConflictConfigurations(data))
            .catch((err) => handleFailedFetch(err));
    };

    const handleSortVehiclesButtonClick = (taggedVehicles, sortValue) => {
        setSelectedSortListValue(sortValue);

        const sortedVehicles = taggedVehicles.sort((a, b) => {
            if (sortValue === "LAST_UPDATED_ASC") {
                return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
            } else if (sortValue === "LAST_UPDATED_DESC") {
                return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
            } else if (sortValue === "CALLSIGN_A_Z") {
                return a.tagged_callsign.localeCompare(b.tagged_callsign);
            } else if (sortValue === "CALLSIGN_Z_A") {
                return b.tagged_callsign.localeCompare(a.tagged_callsign);
            }
        });
        setFilteredTaggedVehicles(sortedVehicles);
    };

    const updateVehicleCardSettings = (sortBy) => {
        const updatedProfileSettings = {
            ...userProfileSettings,
            sidebar: {
                ...userProfileSettings.sidebar,
                vehicle_cards: {
                    sort: sortBy
                }
            }
        };
        updateProfileSettings(updatedProfileSettings);
    };

    return (
        <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
            <Paper sx={{ display: "flex", flexDirection: "row", alignItems: "center", p: 2, borderBottom: "2px solid white" }}>
                {props.mapSidebarPanelIndex === 1 ? (
                    <Tooltip title="View flight cards">
                        <IconButton size="small" onClick={() => props.handleChangeMapSidebarPanel(0)}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <></>
                )}
                <Typography variant="h6" noWrap sx={{ mr: "auto" }}>
                    DAA Enrolled Vehicles
                </Typography>

                <Tooltip title={getTaggedVehiclesSortTooltipFromSortValue(selectedSortListValue)}>
                    <IconButton size="small" onClick={(e) => setSortListAnchorEl(e.currentTarget)}>
                        <SortIcon />
                    </IconButton>
                </Tooltip>

                <Popover
                    open={sortListOpen}
                    anchorEl={sortListAnchorEl}
                    onClose={() => setSortListAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    slotProps={{ paper: { sx: { px: 2, py: 1 } } }}
                    container={props.mapContainer}
                >
                    <FormGroup>
                        {[
                            { label: "Last updated (asc)", value: "LAST_UPDATED_ASC" },
                            { label: "Last updated (desc)", value: "LAST_UPDATED_DESC" },
                            { label: "Callsign A-Z", value: "CALLSIGN_A_Z" },
                            { label: "Callsign Z-A", value: "CALLSIGN_Z_A" }
                        ].map(({ label, value }, filterIdx) => (
                            <FormControlLabel
                                key={filterIdx}
                                label={label}
                                control={
                                    <Checkbox
                                        checked={selectedSortListValue === value}
                                        onChange={() => {
                                            handleSortVehiclesButtonClick(allTaggedVehicles, value);
                                            updateVehicleCardSettings(value);
                                        }}
                                    />
                                }
                            />
                        ))}
                    </FormGroup>
                </Popover>

                <Tooltip title={"Close"}>
                    <IconButton size="small" onClick={() => props.setMapSidebarOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Paper>

            {Array.isArray(filteredTaggedVehicles) && filteredTaggedVehicles.length > 0 ? (
                filteredTaggedVehicles.map((tag, tagIdx) => (
                    <MapTaggedVehicleCard
                        key={tagIdx}
                        tag={tag}
                        map={props.map}
                        handleWatchEntityButtonClick={props.handleWatchEntityButtonClick}
                        activeAlertVolumeConflictionAlerts={props.activeAlertVolumeConflictionAlerts}
                        activeZeroConflictionAlerts={props.activeZeroConflictionAlerts}
                        zeroConflictConfigurations={zeroConflictConfigurations}
                    />
                ))
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 15 }}>
                    <SignalWifiStatusbarConnectedNoInternet4Icon sx={{ fontSize: "7rem" }} />
                    <Typography variant={"body1"}>No vehicles found</Typography>
                </Box>
            )}
        </Box>
    );
};

const MapTaggedVehicleCard = (props) => {
    const [expanded, setExpanded] = useState(false);

    const { publishedOperations } = useMap();
    const { user, handleFailedFetch } = useUserAuth();

    const previousZeroConflictAlertsRef = useRef([]);

    // expand card if logged in as an airspace manager, and there is a new or updated alert
    useEffect(() => {
        const newZeroConflictionAlerts = props.activeZeroConflictionAlerts.get(props.tag.entity_uuid);
        if (user.user_role !== "Airspace Manager" || Array.isArray(newZeroConflictionAlerts) === false) return;

        const isNewAlert = newZeroConflictionAlerts.length > previousZeroConflictAlertsRef.current.length;
        const isHigherSeverityAlert = getHasHigherPriorityZcAlert(previousZeroConflictAlertsRef.current, newZeroConflictionAlerts);

        if (isNewAlert || isHigherSeverityAlert) {
            setExpanded(true);
        }
        previousZeroConflictAlertsRef.current = newZeroConflictionAlerts;
    }, [props.activeZeroConflictionAlerts]);

    const handleWatchVehicleButtonClick = (asdUUID) => {
        props.handleWatchEntityButtonClick(asdUUID);
    };
    const handleDisableDaaAlertsButtonClick = () => {
        const updatedAsdBody = {
            id: props.tag.entity_uuid,
            tagged_callsign: props.tag.tagged_callsign,
            organization: props.tag.organization_id,
            history_locations: [],
            flight_uuid: props.tag.flight_uuid ? props.tag.flight_uuid : "N/A",
            user_id: user.id,
            show_dpz: false,
            alerting_params_uuid: ""
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedAsdBody)
        };
        fetch("api/asd/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .catch((err) => handleFailedFetch(err));
    };
    const feature = GetFeature(props.map, "asd", props.tag.entity_uuid);
    const properties = feature?.getProperties();

    const trackAngle = properties?.asd?.course_deg;
    const groundSpeedKn = properties?.asd?.ground_speed_kn;
    const altAglFt = properties?.asd?.alt_agl_ft;
    const altBaroFt = properties?.asd?.alt_baro_ft;
    const altCorrBaroFt = properties?.asd?.alt_corr_baro_ft;

    const operation = publishedOperations.find((op) => op.flight_uuid === props.tag.flight_uuid);
    const configuration = props.zeroConflictConfigurations.find((config) => config.config_uuid === props.tag.alerting_params_uuid);

    let zcAlerts = [];
    if (props.activeZeroConflictionAlerts.has(props.tag.entity_uuid)) {
        zcAlerts = props.activeZeroConflictionAlerts.get(props.tag.entity_uuid);
    }

    const zcAlertsContainWarning = zcAlerts.find(({ severity }) => severity === "WARNING");
    const inAlertVolume = props.activeAlertVolumeConflictionAlerts.has(props.tag.entity_uuid);
    const border = zcAlertsContainWarning ? "2px solid red" : zcAlerts.length > 0 || inAlertVolume ? "2px solid yellow" : "unset";

    return (
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", border: border, width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                <Box sx={{ flex: 1, mr: "auto", overflow: "auto" }}>
                    <Tooltip title={props.tag.tagged_callsign}>
                        <Typography variant="h6" noWrap>
                            {props.tag.tagged_callsign}
                        </Typography>
                    </Tooltip>

                    <Typography variant="body1" color={"text.secondary"} noWrap>
                        {operation ? `${operation.name}` : "No flight associated"}
                    </Typography>
                </Box>
                {[
                    {
                        title: "Open watch toolbar",
                        icon: <VisibilityIcon />,
                        onClick: () => handleWatchVehicleButtonClick(props.tag.entity_uuid),
                        visible: Boolean(feature) && Array.isArray(feature.getStyle()) && feature.getStyle().length > 0
                    },
                    {
                        title: "Disable DAA Alerts",
                        icon: <NotificationsOffIcon />,
                        onClick: () => handleDisableDaaAlertsButtonClick(),
                        visible:
                            user.user_role === "Admin" ||
                            user.user_role === "Airspace Manager" ||
                            user.user_role === "Operator" ||
                            user.user_role === "Admin - Org"
                    },
                    {
                        title: expanded ? "View less" : "View more",
                        icon: expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />,
                        onClick: () => setExpanded((prev) => !prev),
                        visible: true
                    }
                ].map(({ title, icon, onClick, visible }, i) =>
                    visible ? (
                        <Tooltip key={i} title={title}>
                            <IconButton size="small" onClick={onClick}>
                                {icon}
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <React.Fragment key={i} />
                    )
                )}
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ borderTop: "1px solid white", mt: 1, pt: 1 }}>
                    <Grid container spacing={1}>
                        {[
                            {
                                title: "Track Angle",
                                value: isNaN(trackAngle) === false ? `${trackAngle}°` : "-- °"
                            },
                            {
                                title: "Ground Speed",
                                value: isNaN(groundSpeedKn) === false ? `${groundSpeedKn}kn` : `-- kn`
                            },
                            {
                                title: "M Baro Alt (MSL)",
                                value: isNaN(altBaroFt) === false ? `${altBaroFt}ft` : `-- ft`
                            },
                            {
                                title: "C Baro Alt (MSL)",
                                value: isNaN(altCorrBaroFt) === false ? `${altCorrBaroFt}ft` : `-- ft`
                            },
                            {
                                title: "Altitude (AGL)",
                                value: isNaN(altAglFt) === false ? `${altAglFt}ft` : `-- ft`
                            }
                        ].map(({ title, value }, infoIdx) => (
                            <Grid key={infoIdx} size={6}>
                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                    <Typography variant="body2" color={"text.secondary"} fontWeight={"bold"} noWrap>
                                        {title}:
                                    </Typography>
                                    <Typography variant="body2" color={"text.secondary"} noWrap>
                                        {value}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}

                        <Divider sx={{ width: "100%" }} />

                        <Grid size={12}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Typography variant="body2" color={"text.secondary"} fontWeight={"bold"} noWrap>
                                        Zero Conflict Configuration:
                                    </Typography>

                                    <Typography variant="body2" color={"text.secondary"} noWrap>
                                        {configuration ? `${configuration.name}` : "No configuration found"}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Typography variant="body2" color={"text.secondary"} fontWeight={"bold"} noWrap>
                                        Zero Conflict Alerts:
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color={zcAlerts.length === 0 ? "text.secondary" : zcAlertsContainWarning ? "red" : "yellow"}
                                        noWrap
                                    >
                                        {zcAlerts.length ? `${zcAlerts.length}` : `--`}
                                    </Typography>
                                </Box>

                                {zcAlerts.map((zcAlert, i) => (
                                    <React.Fragment key={i}>
                                        <Divider />

                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                            {[
                                                {
                                                    title: "Severity",
                                                    value: zcAlert.severity,
                                                    color: zcAlert.severity === "WARNING" ? "red" : "yellow"
                                                },
                                                {
                                                    title: "Horizontal Separation",
                                                    value: `${ConvertMetersToFeet(zcAlert.horiz_sep_m)}ft`,
                                                    color: "text.secondary"
                                                },
                                                {
                                                    title: "Vertical Separation",
                                                    value: `${ConvertMetersToFeet(zcAlert.vert_sep_m)}ft`,
                                                    color: "text.secondary"
                                                },
                                                {
                                                    title: "Time until Loss of Separation",
                                                    value: `${zcAlert.time_los_sec}s`,
                                                    color: "text.secondary"
                                                }
                                            ].map(({ title, value, color }, index) => (
                                                <Box key={index} sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" color={"text.secondary"} fontWeight={"bold"} noWrap>
                                                        {title}:
                                                    </Typography>
                                                    <Typography variant="body2" color={color} noWrap>
                                                        {value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Grid>

                        <Divider sx={{ width: "100%" }} />

                        <Grid size={12}>
                            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                <Typography variant="body2" color={"text.secondary"} fontWeight={"bold"} noWrap>
                                    Alert Volume Alert:
                                </Typography>

                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                                    {inAlertVolume === true ? (
                                        <Typography variant="body2" color={"yellow"} noWrap>
                                            {`In Alert Volume`}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color={"text.secondary"} noWrap>
                                            {`--`}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default MapTaggedVehicleCards;
